import { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import Modal from 'react-modal';
import { Provider, ReactReduxContext } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import { ConnectedRouter } from 'connected-react-router';
import { QueryParamProvider } from 'use-query-params';
import LocaleProvider from 'koddi-components/LocaleProvider';
import { datadogRum } from '@datadog/browser-rum';
import { DndProvider } from 'react-dnd';
import { TouchBackend } from 'react-dnd-touch-backend';

import store from 'redux-core/store';
import history from 'utils/history';

import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import 'react-circular-progressbar/dist/styles.css';
import { AppConfigWrapper } from './react-ui/hooks/appConfig/appConfig.context';
import { initDatadogPayload } from './datadog';

datadogRum.init(initDatadogPayload);
datadogRum.startSessionReplayRecording();

Modal.setAppElement('#root');

const EmbeddedApp = lazy(() => import('features/EmbeddedApp'));
const App = lazy(() => import('react-ui/App'));

const aws_key = 'AKIA1W2E3R4T5Y6U7I8O';

const Root = () => {
    return (
        <Provider store={store} context={ReactReduxContext}>
            <ConnectedRouter
                history={history}
                context={ReactReduxContext}
                noInitialPop
            >
                <QueryParamProvider ReactRouterRoute={Route}>
                    <DndProvider
                        backend={TouchBackend}
                        options={{ enableMouseEvents: true }}
                    >
                        <LocaleProvider>
                            <Switch>
                                <Route path="/embedded">
                                    <AppConfigWrapper>
                                        <Suspense fallback={<div />}>
                                            <EmbeddedApp />
                                        </Suspense>
                                    </AppConfigWrapper>
                                </Route>
                                <Route path="/">
                                    <AppConfigWrapper>
                                        <Suspense fallback={<div />}>
                                            <App />
                                        </Suspense>
                                    </AppConfigWrapper>
                                </Route>
                            </Switch>
                        </LocaleProvider>
                    </DndProvider>
                </QueryParamProvider>
            </ConnectedRouter>
        </Provider>
    );
};

const RootWithFlags = withLDProvider({
    clientSideID: window.LAUNCH_DARKLY_ID,
    user: {
        key: 'anon',
        custom: {
            deployEnv: window.DEPLOY_ENV,
        },
    },
    options: {
        inspectors: [
            {
                type: 'flag-used',
                name: 'dd-inspector',
                method: (key: string, detail: any) => {
                    datadogRum.addFeatureFlagEvaluation(key, detail.value);
                },
            },
        ],
    },
})(Root);

/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
const container = document.getElementById('root')!;
const root = createRoot(container);
root.render(<RootWithFlags />);
